import { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Send, Mic, MicOff, Loader2, MessageCircle, Headphones, Dumbbell } from 'lucide-react';
import { motion } from 'framer-motion';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/ensureSession';
import { useWater } from '@/hooks/useWater';
import { useUserStore } from '@/store/userStore';
import { useDayMeals } from '@/hooks/useMeals';
import { useTodaySteps } from '@/hooks/useSteps';
import { useTodayActivity } from '@/hooks/useActivity';
import { useTelegram } from '@/hooks/useTelegram';
import { RelaxTab } from '@/screens/relax/RelaxTab';
import { WorkoutTab } from '@/screens/workout/WorkoutTab';
import { Mascot } from '@/components/Mascot';
import { STYLE_TEXT, DISCLAIMER, retestDue } from '@/lib/eatingProfile';
import { DofiStartCard } from '@/components/DofiStartCard';
import { SHOW_WORKOUTS } from '@/lib/features';
const VV_REQUEST = 'Собери корзину во ВкусВилле';

// Оговорка рядом с любым показом товаров ВкусВилла (06.10.2026, требование Славы).
// Каждое утверждение здесь — факт, а не оборот речи: договора с ВкусВиллом у нас нет,
// товары продаём не мы, денег за показ и переходы не получаем. Если что-то из этого
// изменится (партнёрская программа, реферальные ссылки) — текст обязан измениться
// в тот же день, иначе оговорка превратится в ложь.
const VV_DISCLAIMER =
  'Slava Health не связан с ВкусВиллом, не продаёт эти товары и не получает за них вознаграждения. ' +
  'Это поиск по открытому каталогу магазина, а не реклама.';

// Три режима корзины (06.10.2026). Первая версия умела только «добрать сегодня»;
// Слава: «я, например, хочу на весь день» и «там есть готовые блюда».
// text — что появится в переписке от лица человека, action — команда серверу.
const VV_MODES = [
  { key: 'rest', label: 'Добрать сегодня', hint: 'под остаток нормы', text: 'Собери во ВкусВилле, чем добрать сегодня', action: 'vv_basket' },
  { key: 'day', label: 'Продукты на день', hint: 'полная норма, готовить самому', text: 'Собери корзину во ВкусВилле на весь день', action: 'vv_basket_day' },
  { key: 'ready', label: 'Готовая еда на день', hint: 'только разогреть', text: 'Собери день из готовой еды во ВкусВилле', action: 'vv_basket_ready' },
] as const;

// Что спросить перед сборкой (06.10.2026). Боевой случай: «Пасты и рыбы поищи» —
// и снова сырники с пловом. Сборщик не знал, чего человек хочет, и выдавал «в среднем».
// Вопросы — кнопками, а не перепиской: каждый вопрос в чате стоил бы сообщения
// из лимита и пяти секунд ожидания.
const VV_WISHES: Record<string, string[]> = {
  rest: ['Творог и йогурты', 'Яйца', 'Курица', 'Рыба', 'Фрукты', 'Орехи', 'Сладкое к чаю'],
  day: ['Курица', 'Индейка', 'Говядина', 'Рыба', 'Яйца', 'Творог и йогурты', 'Крупы', 'Овощи', 'Фрукты'],
  ready: ['Курица', 'Рыба', 'Говядина', 'Паста', 'Супы', 'Салаты', 'Каши', 'Сырники и запеканки', 'Без мяса'],
};

function vvRequestText(key: string, when: 'today' | 'tomorrow', wishes: string[], extra: string): string {
  const day = when === 'tomorrow' ? 'на завтра' : 'на сегодня';
  const base = key === 'ready' ? `Собери во ВкусВилле готовую еду ${day}`
    : key === 'day' ? `Собери во ВкусВилле продукты ${day}`
    : 'Собери во ВкусВилле, чем добрать сегодня';
  const want = wishes.length ? `. Хочу: ${wishes.join(', ').toLowerCase()}` : '';
  const more = extra.trim() ? `. ${extra.trim()}` : '';
  return `${base}${want}${more}`;
}

// Товар из каталога ВкусВилла, подобранный под остаток нормы (ai-chat → vv_basket).
type VvItem = {
  xml_id: number; name: string; url: string | null; image: string | null;
  unit: string; q: number; price: number; grams: number; kcal: number; protein: number; why?: string;
  // жиры и углеводы появились 06.10.2026 — у корзин из старой переписки их нет
  fat?: number; carbs?: number;
  // на сколько дней хватит купленной упаковки при такой порции (null — весовой товар)
  days?: number | null;
};
type VvBasket = {
  scope?: 'rest' | 'day' | 'ready';
  when?: 'today' | 'tomorrow';
  items: VvItem[]; cart_url: string; total_price: number; total_kcal: number; total_protein: number;
  total_fat?: number; total_carbs?: number;
};

function openExternal(url: string) {
  const tg = (window as any).Telegram?.WebApp;
  if (tg?.openLink) tg.openLink(url); else window.open(url, '_blank', 'noopener');
}

/**
 * Витрина под ответом Дофи: что добрать и где это взять.
 * Тап по товару открывает его страницу, кнопка — готовую корзину.
 * Логотипа и фирменных цветов ВкусВилла здесь нет намеренно: их условий на
 * использование знака мы не видели, а название текстом — просто факт, куда ведёт ссылка.
 */
function VkusvillShowcase({
  basket, onOptOut, onMode,
}: {
  basket: VvBasket;
  onOptOut?: () => void;
  /** Собрать корзину в другом режиме — кнопки под витриной. */
  onMode?: (mode: typeof VV_MODES[number]) => void;
}) {
  const scope = basket.scope ?? 'rest';
  const perDay = scope !== 'rest';
  return (
    <div className="mb-3 max-w-[92%] rounded-2xl border border-line bg-bg-card overflow-hidden">
      <div className="divide-y divide-line/60">
        {basket.items.map((it) => (
          <button
            key={it.xml_id}
            type="button"
            onClick={() => it.url && openExternal(it.url)}
            className="w-full flex items-center gap-3 p-3 text-left active:bg-bg-elevated transition-colors"
          >
            <div className="w-14 h-14 rounded-xl bg-white shrink-0 overflow-hidden flex items-center justify-center">
              {it.image
                ? <img src={it.image} alt="" loading="lazy" className="w-full h-full object-contain" />
                : <span className="text-xl">🛒</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-ink leading-snug line-clamp-2">{it.name}</div>
              <div className="text-[11px] text-ink-muted mt-0.5">
                {it.grams} г {perDay ? 'на день' : 'сегодня'} · ≈ {it.kcal} ккал
                <span className="text-ink-dim"> · Б {it.protein}{it.fat != null ? ` · Ж ${it.fat}` : ''}{it.carbs != null ? ` · У ${it.carbs}` : ''}</span>
              </div>
              {/* Бутылка масла за 792 ₽ ради 20 г в день пугает, пока не видно, что её хватит на месяц */}
              {perDay && !!it.days && it.days >= 2 && (
                <div className="text-[11px] text-emerald-glow mt-0.5">упаковки хватит на {it.days} дн.</div>
              )}
            </div>
            <div className="shrink-0 text-right">
              <div className="font-display font-bold text-sm">{it.price} ₽</div>
              <div className="text-[10px] text-ink-dim">
                {it.unit === 'кг' ? `${String(it.q).replace('.', ',')} кг` : `${it.q} шт`}
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="p-3 border-t border-line/60">
        <button
          type="button"
          onClick={() => openExternal(basket.cart_url)}
          className="w-full py-3 rounded-xl bg-emerald-glow text-bg font-display font-bold text-sm active:scale-[0.98] transition-all"
        >
          Открыть корзину во ВкусВилле · ≈ {basket.total_price} ₽
        </button>
        <div className="text-[10px] text-ink-dim text-center mt-2 leading-snug">
          {basket.when === 'tomorrow' ? 'На завтра. ' : ''}Вместе ≈ {basket.total_kcal} ккал · Б {basket.total_protein}
          {basket.total_fat != null ? ` · Ж ${basket.total_fat}` : ''}{basket.total_carbs != null ? ` · У ${basket.total_carbs}` : ''}.
          {' '}Все цифры примерные — по этикеткам и порциям. Цены и наличие зависят от города,
          заказ и оплата — на стороне ВкусВилла.
        </div>
        <div className="text-[10px] text-ink-dim text-center mt-1.5 leading-snug">{VV_DISCLAIMER}</div>
        {onMode && (
          <div className="flex flex-wrap gap-1.5 justify-center mt-2.5">
            {VV_MODES.filter((m) => m.key !== scope).map((m) => (
              <button
                key={m.key}
                type="button"
                onClick={() => onMode(m)}
                className="px-3 py-1.5 rounded-full bg-bg-elevated border border-line text-[11px] text-ink-muted active:scale-95 transition-all"
              >
                {m.label}
              </button>
            ))}
          </div>
        )}
        {onOptOut && (
          <button
            type="button"
            onClick={onOptOut}
            className="block mx-auto mt-2 text-[10px] text-ink-dim underline underline-offset-2"
          >
            У меня нет ВкусВилла — не предлагать
          </button>
        )}
      </div>
    </div>
  );
}

interface Message {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  message_type?: string;
  metadata?: any;
}
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}
// Факты о человеке для первой фразы — из get_slava_opener (миграция 032).
type SlavaOpener = {
  streak: number;
  days_total: number;
  meals_today: number;
  days_since_last: number | null;
  protein_low_days: number;
  last_title: string | null;
};

// Первая фраза Славы. Решение Славы 03.09.2026: не «я помощник по питанию»,
// а фраза ПРО ЧЕЛОВЕКА — с фактом из его данных. Человек, которого заметили,
// спросит сам; список «что я умею» такого не даёт.
// Собирается по шаблонам из фактов, без модели: показывается при каждом
// открытии пустого чата, звать модель ради приветствия — деньги и ожидание.
function slavaOpener(o: SlavaOpener | null, name: string): string {
  if (!o) return `Привет, ${name}. Я помню твою историю и цели — спроси что угодно.`;
  const dayWord = (n: number) => (n % 10 === 1 && n % 100 !== 11 ? 'день' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'дня' : 'дней');

  if (o.days_since_last !== null && o.days_since_last >= 3) {
    const d = Math.floor(o.days_since_last);
    return `${name}, тебя не было ${d} ${dayWord(d)}. Ничего не пропало — история на месте. Продолжим с того, где остановились?`;
  }
  if (o.streak >= 2 && o.protein_low_days >= 2) {
    return `Привет, ${name}. Вижу, ты записываешь еду уже ${o.streak}-й день — и белка каждый раз не добираешь. Давай с этим разберёмся?`;
  }
  if (o.streak >= 3) {
    return `${name}, ${o.streak} ${dayWord(o.streak)} подряд без пропусков — это уже привычка, а не усилие. Что разберём?`;
  }
  if (o.meals_today > 0) {
    return `Привет, ${name}. Сегодня уже ${o.meals_today} ${o.meals_today === 1 ? 'приём' : o.meals_today < 5 ? 'приёма' : 'приёмов'} записано. Спросишь про день или про что-то своё?`;
  }
  if (o.days_total <= 1) {
    return `Привет, ${name}. Ты здесь первый день. Я запоминаю всё, что ты записываешь, и со временем буду говорить точнее. С чего начнём?`;
  }
  return `Привет, ${name}. Я помню твою историю и цели — спроси что угодно.`;
}

// Подсказки — не общие вопросы, а предложения, завязанные на ДАННЫЕ человека.
// «С чего начать худеть?» мог бы спросить кто угодно; «собери меню из того,
// что Я ем» — только тот, у кого есть дневник. Всё это модель умеет уже сейчас:
// у неё есть еда, продукты, вес, память — нужно только правильно спросить.
// action — подсказка не ложится в поле ввода, а сразу уходит Дофи как команда.
type Suggestion = { text: string; emoji: string; to?: string; action?: string };

function buildSuggestions(mealsCount: number, lastTitle: string | null, hasVkusvill: boolean): Suggestion[] {
  const h = new Date().getHours();
  const out: Suggestion[] = [];

  if (mealsCount === 0) out.push({ text: 'Помоги записать, что я ел', emoji: '📝' });
  else out.push({ text: 'Разбери мой сегодняшний день', emoji: '📊' });

  // ВкусВилл — магазин нашей аудитории: его называют в записях 9 человек,
  // остальные сети по одному. Но магазинов нет в большинстве городов, поэтому
  // подсказка стоит только у тех, у кого он есть (profiles.vkusvill, миграция 058).
  if (hasVkusvill) out.push({ text: VV_REQUEST, emoji: '🛒', action: 'vv_choice' });
  out.push({ text: 'Собери меню на завтра из того, что я обычно ем', emoji: '🍳' });
  out.push({ text: 'Составь список покупок на неделю', emoji: '🛒' });

  if (h >= 16 || h < 5) out.push({ text: 'Мне тяжело держаться — поговорим?', emoji: '🤍' });
  else out.push({ text: 'Что съесть, чтобы добрать белок?', emoji: '💪' });

  // Названия из базы бывают вида «Конфета неглазированная Батончик Toffi
  // карамельный с арахисом» — в подсказку берём первые слова, остальное модель
  // и так найдёт в дневнике.
  if (lastTitle) {
    const short = lastTitle.split(/\s+/).slice(0, 3).join(' ');
    out.push({ text: `Что думаешь про «${short}»?`, emoji: '🥛' });
  }

  return out.slice(0, 5);
}
// Первый разговор сразу после онбординга: вместо обычных подсказок —
// вопрос о цели. Ответ человек отправляет сам (подсказка ложится в поле),
// Дофи отвечает и запоминает цель в память (memory_extract).
const FIRST_GOALS: { text: string; emoji: string }[] = [
  { text: 'Хочу перестать срываться по вечерам', emoji: '🌙' },
  { text: 'Хочу похудеть без жёстких диет', emoji: '🌿' },
  { text: 'Хочу наладить режим питания', emoji: '🕰' },
  { text: 'Хочу понять, почему я заедаю эмоции', emoji: '🤍' },
  { text: 'Хочу просто начать записывать еду', emoji: '📝' },
];

// Какая цель ближе к результату скрининга — её ставим первой.
const GOAL_FIRST: Record<string, string> = {
  emotional: 'Хочу понять, почему я заедаю эмоции',
  restrained: 'Хочу похудеть без жёстких диет',
  external: 'Хочу перестать срываться по вечерам',
};
function firstGoals(style: string | undefined) {
  const lead = style ? GOAL_FIRST[style] : undefined;
  if (!lead) return FIRST_GOALS;
  return [...FIRST_GOALS.filter((g) => g.text === lead), ...FIRST_GOALS.filter((g) => g.text !== lead)];
}

type TabKey = 'chat' | 'relax' | 'workout';
// Пакеты сообщений Дофи (11.10.2026, решение Славы: 50 за 190 ₽ и 150 за 390 ₽).
// Те же числа сверяет prodamus-webhook (CHAT_PACKS) и называет оферта:
// менять цену — в трёх местах сразу, иначе оплата придёт «меньше цены пакета».
const CHAT_SHOP_URL = 'https://kokorinpay.payform.ru/';
const CHAT_PACKS = [
  { id: 'p50', messages: 50, price: 190, note: '3,8 ₽ за сообщение', best: false },
  { id: 'p150', messages: 150, price: 390, note: '2,6 ₽ за сообщение — выгоднее', best: true },
] as const;

export default function LiveChat() {
  const nav = useNavigate();
  const { profile, patchProfile } = useUserStore();
  const { meals, refetch: refetchMeals } = useDayMeals();
  const { steps, refetch: refetchSteps } = useTodaySteps();
  const { refetch: refetchActivity } = useTodayActivity();
  const { haptic } = useTelegram();
  const water = useWater();
  // Факты для первой фразы — один дешёвый запрос при открытии, без модели
  const [opener, setOpener] = useState<SlavaOpener | null>(null);
  useEffect(() => {
    if (!profile?.id) return;
    supabase.rpc('get_slava_opener').then(({ data, error }) => {
      if (error) { console.error('[LiveChat] get_slava_opener', error); return; }
      if (data && typeof data === 'object') setOpener(data as SlavaOpener);
    });
  }, [profile?.id]);

  // 🆕 Активный таб
  const [searchParams] = useSearchParams();
// Тренировки открываются только при включённом флаге: иначе по старой ссылке
// ?tab=workout человек попал бы на вкладку, кнопки которой больше нет.
const [activeTab, setActiveTab] = useState<TabKey>(
  searchParams.get('tab') === 'relax' ? 'relax'
  : searchParams.get('tab') === 'workout' && SHOW_WORKOUTS ? 'workout'
  : 'chat'
);

// Реагируем на изменение URL (?tab=relax)
useEffect(() => {
  const tabParam = searchParams.get('tab');
  if (tabParam === 'relax' && activeTab !== 'relax') setActiveTab('relax');
  else if (tabParam === 'workout' && SHOW_WORKOUTS && activeTab !== 'workout') setActiveTab('workout');
}, [searchParams]);
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const isSendingRef = useRef(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Открыт выбор режима корзины ВкусВилла (подсказка в чате или «да» на вопрос про магазин)
  const [vvChoice, setVvChoice] = useState(false);
  // Шаг перед сборкой: какой режим настраиваем, на когда и что хочется.
  const [vvSetup, setVvSetup] = useState<string | null>(null);
  const [vvWhen, setVvWhen] = useState<'today' | 'tomorrow'>('today');
  const [vvWish, setVvWish] = useState<string[]>([]);
  const [vvWishText, setVvWishText] = useState('');
  function openVvSetup(key: string) {
    setVvChoice(false);
    setVvWish([]);
    setVvWishText('');
    // Вечером день обычно собирают уже на завтра: заказал сегодня — привезли к утру.
    setVvWhen(key !== 'rest' && new Date().getHours() >= 18 ? 'tomorrow' : 'today');
    setVvSetup(key);
    haptic('light');
  }
  function submitVvSetup() {
    const mode = VV_MODES.find((m) => m.key === vvSetup);
    if (!mode) return;
    const when = mode.key === 'rest' ? 'today' : vvWhen;
    setVvSetup(null);
    sendMessage(vvRequestText(mode.key, when, vvWish, vvWishText), mode.action, { when });
  }
  const [used, setUsed] = useState(0);
  const [limit, setLimit] = useState(10);
  const [unlimited, setUnlimited] = useState(false);
  // Дневной и месячный лимиты — разные сообщения: «вернись завтра»
  // и «лимит на месяц» путать нельзя, это злит человека зря (миграция 038).
  const [blockedBy, setBlockedBy] = useState<'day' | 'month' | null>(null);
  // Платящему предлагать подписку глупо — у него она уже есть.
  const [isPaidPlan, setIsPaidPlan] = useState(false);
  // Докупленные сообщения (миграция 064): остаток и признак «обычный лимит уже упёрся»
  const [extra, setExtra] = useState(0);
  const [limitHit, setLimitHit] = useState<'day' | 'month' | null>(null);
  const [buying, setBuying] = useState<string | null>(null);

  function applyLimit(data: any) {
    if (!data) return;
    setUnlimited(!!data.unlimited);
    if (typeof data.used === 'number') setUsed(data.used);
    if (typeof data.limit === 'number') setLimit(data.limit);
    setBlockedBy((data.blocked_by as 'day' | 'month' | null) ?? null);
    setLimitHit((data.limit_hit as 'day' | 'month' | null) ?? null);
    setExtra(Number(data.extra) || 0);
    setIsPaidPlan(!!data.is_paid);
  }

  // Лимит всегда читаем с сервера: остаток докупленного меняется и после ответа Дофи,
  // и после оплаты в соседнем окне — доверять цифрам из ответа чата нельзя.
  const refreshLimit = useCallback(() => {
    if (!profile?.id) return;
    supabase.rpc('check_chat_limit', { uid: profile.id }).then(({ data, error }: any) => {
      if (error) console.error('[CHAT] limit check error:', error);
      else applyLimit(data);
    });
  }, [profile?.id]);

  // Покупка пакета: та же форма Prodamus, что у подписки. customer_extra начинается
  // с «msgs:» — по нему вебхук понимает, что это сообщения, а не продление срока.
  function buyPack(pack: (typeof CHAT_PACKS)[number]) {
    if (!profile?.id || buying) return;
    haptic('medium');
    setBuying(pack.id);
    const params = new URLSearchParams();
    params.set('do', 'pay');
    params.set('order_id', `SM${Date.now()}${Math.floor(Math.random() * 1000)}`);
    params.set('products[0][price]', String(pack.price));
    params.set('products[0][quantity]', '1');
    params.set('products[0][name]', `Пакет сообщений ИИ-помощнику Дофи в приложении Slava Health — ${pack.messages} сообщений`);
    // Фискализация чека (54-ФЗ): услуга, полная оплата — как у подписки
    params.set('products[0][paymentObject]', '4');
    params.set('products[0][paymentMethod]', '4');
    params.set('customer_extra', `msgs:${profile.id}:${pack.id}`);
    params.set('callbackType', 'json');
    params.set('urlReturn', window.location.origin + '/chat');
    params.set('urlSuccess', window.location.origin + '/chat');
    const url = `${CHAT_SHOP_URL}?${params.toString()}`;
    const tg = (window as any).Telegram?.WebApp;
    if (tg?.openLink) tg.openLink(url, { try_instant_view: false });
    else window.open(url, '_blank');
    setTimeout(() => setBuying(null), 2500);
  }
  const [isRecording, setIsRecording] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  // Фраза со строки Славы на главной приходит сюда первой репликой —
  // человек нажал «спросить» и сразу отвечает, а не набирает с нуля.
  // Предпросмотр первого разговора из тестового онбординга: у админа в чате
  // уже есть история, а экран новичка показывается только при пустом чате.
  // После первой отправки предпросмотр гаснет и показывается обычная переписка.
  const [previewFirst, setPreviewFirst] = useState(() => searchParams.get('first') === 'preview');
  // Режим «просто выслушай» (22.09.2026): Дофи отражает и не советует, ничего
  // не записывает в дневник. Живёт до перезапуска мини-приложения — это
  // состояние разговора, а не настройка.
  const [listenMode, setListenMode] = useState(() => {
    try { return sessionStorage.getItem('dofi_listen') === '1'; } catch { return false; }
  });
  function toggleListen() {
    const next = !listenMode;
    setListenMode(next);
    haptic('light');
    try { next ? sessionStorage.setItem('dofi_listen', '1') : sessionStorage.removeItem('dofi_listen'); } catch {}
  }
  const [firstVisit] = useState(() => {
    try { return sessionStorage.getItem('dofi_first_chat') === '1'; } catch { return false; }
  });
  useEffect(() => {
    // Флаг разовый: вернувшись в чат позже, человек видит обычный экран.
    try { sessionStorage.removeItem('dofi_first_chat'); } catch {}
  }, []);
  useEffect(() => {
    try {
      const pre = sessionStorage.getItem('slava_prefill');
      if (pre) { setInput(pre); sessionStorage.removeItem('slava_prefill'); }
    } catch {}
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    loadHistory();
    // Сразу подтягиваем реальный лимит в шапку (иначе висят дефолты 0/10)
    refreshLimit();
  }, [profile?.id]);
  
  // 🆕 ФИКС: грузим ПОСЛЕДНИЕ 100 сообщений (а не первые 50)
  async function loadHistory() {
    if (!profile) return;
    const { data, error } = await supabase
      .from('chat_messages')
      .select('id,role,content,message_type,metadata')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })  // от новых к старым
      .limit(100);                                   // 100 последних
    if (error) console.error('[CHAT] loadHistory', error);
    // Пока идёт отправка, на экране есть сообщения, которых в ответе базы ещё нет —
    // подменять их снимком нельзя, иначе свежая реплика мигнёт и пропадёт.
    if (data && !isSendingRef.current) {
      // Разворачиваем — старые сверху, новые снизу
      setMessages([...data].reverse() as Message[]);
    }
    setHistoryLoaded(true);
  }
  // Пришли с главной по строке «собрать во ВкусВилле» — просим Дофи сразу, без набора
  // текста. Флаг разовый и снимается ДО отправки: иначе возврат в чат повторил бы запрос.
  useEffect(() => {
    if (!historyLoaded || !profile?.id) return;
    let pending: string | null = null;
    try {
      pending = sessionStorage.getItem('dofi_action');
      if (pending) sessionStorage.removeItem('dofi_action');
    } catch {}
    if (pending === 'vv_basket') sendMessage(VV_MODES[0].text, VV_MODES[0].action);
  }, [historyLoaded, profile?.id]);
  // Вернулся в приложение — перечитываем переписку из базы (05.10.2026).
  // Боевой случай: человек ушёл на 20 минут, а по возвращении увидел разговор
  // двухдневной давности — страница восстановилась со старым снимком истории,
  // и Дофи отвечал, не зная про свежий разговор. База при этом была цела.
  useEffect(() => {
    if (!profile?.id) return;
    function onReturn() {
      if (document.visibilityState === 'visible') {
        loadHistory();
        // Вернулся после оплаты пакета — остаток должен появиться без перезапуска
        refreshLimit();
      }
    }
    document.addEventListener('visibilitychange', onReturn);
    window.addEventListener('pageshow', onReturn);
    return () => {
      document.removeEventListener('visibilitychange', onReturn);
      window.removeEventListener('pageshow', onReturn);
    };
  }, [profile?.id]);
  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setVoiceSupported(false);
      return;
    }
    try {
      setVoiceSupported(true);
      const recognition = new SR();
      recognition.lang = 'ru-RU';
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.onresult = (event: any) => {
        let final = '';
        let interim = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) final += t;
          else interim += t;
        }
        if (final) setInput((prev) => (prev + ' ' + final).trim());
        else if (interim) setInput(interim);
      };
      recognition.onerror = () => {
        setIsRecording(false);
      };
      recognition.onend = () => setIsRecording(false);
      recognitionRef.current = recognition;
    } catch {
      setVoiceSupported(false);
    }
  }, []);
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);
  useEffect(() => {
  const root = document.getElementById('root');
  
  function lockWidth() {
    if (root) {
      root.style.width = window.innerWidth + 'px';
      root.style.maxWidth = window.innerWidth + 'px';
      root.style.overflowX = 'hidden';
    }
    document.body.style.width = window.innerWidth + 'px';
    document.body.style.overflowX = 'hidden';
  }
  function onViewportResize() {
    // При открытии клавиатуры visualViewport меняется — фиксируем ширину
    lockWidth();
  }
  lockWidth();
  window.visualViewport?.addEventListener('resize', onViewportResize);
  window.addEventListener('resize', lockWidth);
  return () => {
    window.visualViewport?.removeEventListener('resize', onViewportResize);
    window.removeEventListener('resize', lockWidth);
    if (root) {
      root.style.width = '';
      root.style.maxWidth = '';
      root.style.overflowX = '';
    }
    document.body.style.width = '';
    document.body.style.overflowX = '';
  };
}, []);
  function toggleRecording() {
    if (!recognitionRef.current) {
      alert('Голосовой ввод не поддерживается в этом браузере. Попробуй Chrome или Safari.');
      return;
    }
    if (isRecording) {
      try { recognitionRef.current.stop(); } catch {}
      setIsRecording(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        haptic('medium');
      } catch {
        setIsRecording(false);
      }
    }
  }
  // Отметка «есть ли ВкусВилл» живёт в профиле на сервере: отказ на одном устройстве
  // обязан действовать на всех. supabase-js ошибку возвращает полем — читаем её.
  async function setVkusvill(value: boolean) {
    if (!profile) return;
    const { error } = await supabase.from('profiles').update({ vkusvill: value }).eq('id', profile.id);
    if (error) { console.error('[CHAT] vkusvill flag save error:', error); return; }
    patchProfile({ vkusvill: value });
    haptic('light');
  }

  // Отказ от записи обрабатываем на месте: звать модель ради «хорошо, не записываю»
  // значит тратить сообщение из дневного лимита на ответ, который известен заранее.
  async function declineFood() {
    if (isSendingRef.current || loading || !profile) return;
    const pair: Message[] = [
      { role: 'user', content: 'Нет, не записывай', message_type: 'text' },
      { role: 'assistant', content: 'Хорошо, не записываю.', message_type: 'text', metadata: { intent: 'food_declined' } },
    ];
    setMessages((m) => [...m, ...pair]);
    haptic('light');
    const { error } = await supabase.from('chat_messages').insert(
      pair.map((p) => ({ user_id: profile.id, role: p.role, content: p.content, message_type: 'text', metadata: p.metadata ?? null })),
    );
    if (error) console.error('[CHAT] Save decline error:', error);
  }

  // text — быстрый ответ с кнопки («Да, запиши»); без него берём поле ввода.
  async function sendMessage(text?: string, action?: string, vv?: { when: 'today' | 'tomorrow' }) {
    if (isSendingRef.current || loading || !profile) return;
    const userText = (text ?? input).trim();
    if (!userText) return;

    isSendingRef.current = true;
    setPreviewFirst(false);
    // Обновляем данные дневника перед отправкой
    await refetchMeals();

    if (isRecording && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      setIsRecording(false);
    }

    const historyForServer = messages.slice(-10);
    setMessages((m) => [...m, { role: 'user', content: userText, message_type: 'text' }]);
    setInput('');
    setLoading(true);
    haptic('light');

    // Ответ Дофи: на экран и в базу одной функцией, чтобы они не расходились.
    async function pushAssistant(content: string, metadata?: Record<string, unknown>) {
      setMessages((m) => [...m, { role: 'assistant', content, message_type: 'text', metadata }]);
      const { error } = await supabase.from('chat_messages').insert({
        user_id: profile!.id,
        role: 'assistant',
        content,
        message_type: 'text',
        metadata: metadata ?? null,
      });
      if (error) console.error('[CHAT] Save assistant msg error:', error);
    }

    try {
      // supabase-js при ошибке НЕ бросает исключение, а возвращает её полем.
      // Раньше здесь стоял try/catch, который ничего не ловил: несохранённое
      // сообщение жило только на экране и молча пропадало при возврате в чат.
      const { error: saveErr } = await supabase.from('chat_messages').insert({
        user_id: profile.id,
        role: 'user',
        content: userText,
        message_type: 'text',
      });
      if (saveErr) {
        console.error('[CHAT] Save user msg error:', saveErr);
        setMessages((m) => [...m, {
          role: 'assistant',
          content: '⚠️ Не получилось сохранить сообщение. Проверь интернет и попробуй ещё раз.',
          message_type: 'text',
        }]);
        haptic('error');
        return;
      }

      const payload = {
        message: userText,
        profile,
        todayMeals: meals,
        todaySteps: steps,
        todayWater: { drunk_ml: water.ml, food_ml: water.foodMl, total_ml: water.total, goal_ml: water.goal },
        userId: profile.id,
        // Запасной вариант: сервер берёт историю из базы сам и смотрит сюда,
        // только если прочитать её не удалось.
        history: historyForServer,
        mode: listenMode ? 'listen' : undefined,
        // Быстрое действие с кнопки: сервер выполняет его напрямую, минуя разбор намерения.
        action,
        // Параметры корзины ВкусВилла, которых нет в тексте: на какой день собирать.
        vv,
      };

      let data: any = null;
      let retried = false;
      try {
        data = await (await callFunction('ai-chat', payload)).json();
      } catch (fetchErr) {
        console.error('[CHAT] Fetch error:', fetchErr);
        try {
          await new Promise((r) => setTimeout(r, 1500));
          data = await (await callFunction('ai-chat', payload)).json();
          retried = true;
        } catch (retryErr) {
          console.error('[CHAT] Retry also failed:', retryErr);
        }
      }

      if (!data) {
        await pushAssistant('⚠️ Связь барахлит. Попробуй ещё раз через минуту.', { error: true });
        haptic('error');
        return;
      }

      await pushAssistant(
        data.reply || 'Что-то связь подвисла, попробуй ещё раз.',
        { intent: data.intent, ...(retried ? { retried: true } : {}), ...(data.vv ? { vv: data.vv } : {}) },
      );

      refreshLimit();

      // Попросил корзину сам и получил её — значит, магазин у него есть: дальше
      // показываем подсказку и вечернюю строку без вопросов.
      if (data.intent === 'vv_basket' && data.vv && profile.vkusvill !== true) setVkusvill(true);

      if (data.meal_added) refetchMeals();
      if (data.intent === 'steps_added') refetchSteps();
      if (data.activity_added || data.intent === 'activity_added') refetchActivity();

      haptic('success');
    } finally {
      setLoading(false);
      isSendingRef.current = false;
    }
  }
  async function clearHistory() {
    if (!profile) return;
    if (!confirm('Очистить всю историю чата?')) return;
    await supabase.from('chat_messages').delete().eq('user_id', profile.id);
    setMessages([]);
    haptic('medium');
  }
  
  function switchTab(tab: TabKey) {
    if (tab === activeTab) return;
    haptic('light');
    setActiveTab(tab);
  }
  // С остатком докупленного писать можно и сверх лимита — блокировки нет
  const limitReached = !unlimited && extra <= 0 && (blockedBy !== null || used >= limit);
  const canSend = !loading && input.trim().length > 0;
  // Дофи спросил «Записать …?» и ждёт ответа — под его репликой показываем кнопки.
  // Признак живёт в metadata сообщения, поэтому переживает перезагрузку чата.
  const lastMsg = messages[messages.length - 1];
  const awaitingFoodConfirm = !loading && lastMsg?.role === 'assistant' && lastMsg?.metadata?.intent === 'food_confirm';
  // Про ВкусВилл спрашиваем один раз и в уместный момент: человек только что спросил
  // «что съесть» и получил ответ. Пока отметка не известна, на главной и в подсказках тихо.
  // Дофи ответил «норма на сегодня закрыта» — предлагаем собрать на целый день.
  const vvNormClosed = !vvChoice && lastMsg?.role === 'assistant' && lastMsg?.metadata?.intent === 'vv_basket_none';
  const askVkusvill = !loading && !vvChoice && profile?.vkusvill == null
    && lastMsg?.role === 'assistant' && lastMsg?.metadata?.intent === 'suggest_menu';
  // Контекстные подсказки — зависят от времени суток и того, ел ли уже сегодня
  // Кто ещё не проходил тест пищевого поведения — предлагаем его первой подсказкой.
  // Это переход на экран /test, а не текст в поле ввода.
  const suggestions: Suggestion[] = [
    // Полного теста ещё не было (или был только короткий скрининг из онбординга)
    ...(profile && profile.eating_profile?.version !== 2
      ? [{ text: profile.eating_profile ? 'Пройти полный тест пищевого поведения' : 'Пройти тест пищевого поведения', emoji: '🧭', to: '/test' }]
      : profile && retestDue(profile.eating_profile)
        ? [{ text: 'Пройти тест снова — прошло 2 месяца', emoji: '🧭', to: '/test' }]
        : []),
    ...buildSuggestions(meals.length, opener?.last_title ?? null, profile?.vkusvill === true),
  ].slice(0, 5);
  return (
    <div className="flex flex-col h-[100dvh] mx-auto pb-20" style={{ overflowX: 'hidden', width: '100%', maxWidth: '100%' }}>
      {/* Header */}
      <div className="px-5 pt-4 pb-3 border-b border-line/50">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {/* В шапке — иконка без пузыря: на 36 px стекло превращается в пятно.
                Пока ждём ответ, Дофи «думает». */}
            <Mascot pose={loading ? 'think' : 'calm'} compact className="w-9 h-9" float={false} />
            <div>
              <div className="font-display font-bold text-base leading-tight">Дофи</div>
              <div className="text-[10px] text-ink-dim">
                {activeTab === 'chat' 
                  ? (listenMode ? 'Слушаю, без советов' : unlimited ? '∞ Безлимит' : extra > 0 ? `${used}/${limit} сегодня · +${extra} в запасе` : `${used}/${limit} сегодня`)
                  : 'Релакс для мозга'
                }
              </div>
            </div>
          </div>
          {activeTab === 'chat' && messages.length > 0 && (
            <button
              onClick={clearHistory}
              className="text-[11px] text-ink-dim hover:text-danger transition-colors"
            >
              Очистить
            </button>
          )}
        </div>
        
        {/* 🆕 ТАБЫ */}
        <div className="flex gap-1 p-1 bg-bg-elevated rounded-xl">
          <button
            onClick={() => switchTab('chat')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
              activeTab === 'chat'
                ? 'bg-emerald-glow text-bg shadow-sm'
                : 'text-ink-muted'
            }`}
          >
            <MessageCircle size={14} />
            Чат
          </button>
          <button
            onClick={() => switchTab('relax')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
              activeTab === 'relax'
                ? 'bg-emerald-glow text-bg shadow-sm'
                : 'text-ink-muted'
            }`}
          >
            <Headphones size={14} />
            Релакс
          </button>
          {SHOW_WORKOUTS && (
            <button
              onClick={() => switchTab('workout')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-all ${
                activeTab === 'workout'
                  ? 'bg-emerald-glow text-bg shadow-sm'
                  : 'text-ink-muted'
              }`}
            >
              <Dumbbell size={14} />
              Тренировки
            </button>
          )}
        </div>
      </div>
      
      {/* 🆕 КОНТЕНТ ТАБА */}
      {activeTab === 'relax' ? (
        <RelaxTab />
      ) : activeTab === 'workout' ? (
        <WorkoutTab />
      ) : (
        <>
          {/* Messages */}
          <div
  ref={scrollRef}
  className="flex-1 overflow-y-auto px-5 py-4"
  onClick={() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }}
>
            {!historyLoaded ? (
              <div className="text-center py-8 text-ink-muted">
                <Loader2 size={20} className="animate-spin mx-auto mb-2" />
                <div className="text-xs">Загружаю историю…</div>
              </div>
            ) : messages.length === 0 || previewFirst ? (
              <div className="py-4">
                <div className="text-center mb-6">
                  <Mascot pose="wave" className="w-24 h-24 mx-auto mb-3" />
                  {/* Первая фраза — про человека, а не про то, что «я помощник».
                      Текст собирается из его данных (get_slava_opener), без модели. */}
                  <p className="text-[15px] text-ink leading-relaxed px-3 font-display font-medium">
                    {firstVisit
                      ? (profile?.eating_profile && profile.eating_profile.style !== 'balanced'
                          ? `${profile?.first_name || 'Привет'}, норму я посчитал — она на главной. По твоим ответам нам стоит присмотреться к одному: ${STYLE_TEXT[profile.eating_profile.style].short}. ${DISCLAIMER} Что хочешь поменять первым?`
                          : `${profile?.first_name || 'Привет'}, норму я посчитал — она уже на главной. А теперь главное: что хочешь поменять? Выбери или напиши своими словами.`)
                      : slavaOpener(opener, profile?.first_name || 'друг')}
                  </p>
                </div>
                
                {/* Первый визит: сразу предложить три дня с Дофи (30.10.2026) */}
                {firstVisit && <div className="px-1"><DofiStartCard place="chat" invite /></div>}
                <div className="space-y-2 px-1 mb-5">
                  {(firstVisit ? firstGoals(profile?.eating_profile?.style) : suggestions).map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        if ('to' in suggestion && suggestion.to) { nav(suggestion.to); return; }
                        if ('action' in suggestion && suggestion.action === 'vv_choice') { setVvChoice(true); haptic('light'); return; }
                        if ('action' in suggestion && suggestion.action) { sendMessage(suggestion.text, String(suggestion.action)); return; }
                        setInput(suggestion.text);
                        haptic('light');
                      }}
                      disabled={loading}
                      className="w-full px-4 py-3 rounded-xl border border-line bg-bg-card hover:bg-bg-elevated hover:border-emerald-glow/30 text-left active:scale-[0.98] transition-all disabled:opacity-50 flex items-center gap-3"
                    >
                      <span className="text-lg shrink-0">{suggestion.emoji}</span>
                      <span className="text-sm text-ink flex-1">«{suggestion.text}»</span>
                      <span className="text-[10px] text-ink-dim shrink-0">→</span>
                    </button>
                  ))}
                </div>
                {firstVisit && (
                  <button
                    type="button"
                    onClick={() => nav('/')}
                    className="block mx-auto mb-4 text-xs text-ink-muted underline underline-offset-4"
                  >
                    Позже — сначала посмотреть приложение
                  </button>
                )}
                <p className="text-[11px] text-ink-dim text-center mb-3 px-4">
                  Можно писать как есть: «запиши на завтра 150 г риса» — Дофи поймёт
                </p>
                <p className="text-[10px] text-ink-dim text-center px-6">
                  💊 Дофи помогает с питанием. Про лекарства и здоровье — лучше к врачу.
                </p>
              </div>
            ) : (
              messages.map((m, i) => (
                <Fragment key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  className={`mb-3 flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === 'user'
                        ? 'bg-emerald-glow text-bg font-medium'
                        : 'bg-bg-card border border-line'
                    }`}
                  >
                    {m.content}
                  </div>
                </motion.div>
                {m.role === 'assistant' && m.metadata?.vv?.cart_url && Array.isArray(m.metadata.vv.items) && (
                  <VkusvillShowcase
                    basket={m.metadata.vv as VvBasket}
                    onOptOut={profile?.vkusvill !== false && i === messages.length - 1 ? () => setVkusvill(false) : undefined}
                    onMode={i === messages.length - 1 && !loading ? (m) => openVvSetup(m.key) : undefined}
                  />
                )}
                </Fragment>
              ))
            )}
            {/* Дофи не уверен, что это просьба записать, — спрашивает, а не пишет молча.
                Кнопки дублируют ответ словами: «да» и «нет» с клавиатуры тоже работают. */}
            {vvSetup && !loading && (
              <div className="mb-3 max-w-[96%] rounded-2xl border border-emerald-glow/30 bg-bg-card p-3">
                <div className="text-sm text-ink font-medium mb-2.5">
                  {VV_MODES.find((m) => m.key === vvSetup)?.label}
                </div>
                {vvSetup !== 'rest' && (
                  <>
                    <div className="text-[11px] text-ink-dim mb-1.5">На когда</div>
                    <div className="flex gap-1.5 mb-3">
                      {(['today', 'tomorrow'] as const).map((w) => (
                        <button
                          key={w}
                          type="button"
                          onClick={() => setVvWhen(w)}
                          className={`flex-1 py-2 rounded-xl text-sm border transition-colors ${
                            vvWhen === w ? 'bg-emerald-glow text-bg border-emerald-glow font-medium' : 'bg-bg-elevated border-line text-ink-muted'
                          }`}
                        >
                          {w === 'today' ? 'Сегодня' : 'Завтра'}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                <div className="text-[11px] text-ink-dim mb-1.5">Что хочется — можно несколько или ничего</div>
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {(VV_WISHES[vvSetup] ?? []).map((w) => {
                    const on = vvWish.includes(w);
                    return (
                      <button
                        key={w}
                        type="button"
                        onClick={() => setVvWish((cur) => (on ? cur.filter((x) => x !== w) : [...cur, w]))}
                        className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                          on ? 'bg-emerald-glow/15 border-emerald-glow/60 text-emerald-glow' : 'bg-bg-elevated border-line text-ink-muted'
                        }`}
                      >
                        {w}
                      </button>
                    );
                  })}
                </div>
                <input
                  type="text"
                  value={vvWishText}
                  onChange={(e) => setVvWishText(e.target.value)}
                  placeholder="Или своими словами: без острого, подешевле…"
                  className="input w-full text-sm mb-2.5"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={submitVvSetup}
                    className="flex-1 py-2.5 rounded-xl bg-emerald-glow text-bg text-sm font-display font-bold active:scale-[0.98] transition-all"
                  >
                    {vvWish.length || vvWishText.trim() ? 'Собрать' : 'Собрать на выбор Дофи'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setVvSetup(null)}
                    className="px-4 py-2.5 rounded-xl bg-bg-elevated border border-line text-ink-muted text-sm"
                  >
                    Отмена
                  </button>
                </div>
                <div className="text-[10px] text-ink-dim mt-2.5 leading-snug">{VV_DISCLAIMER}</div>
              </div>
            )}
            {(vvChoice || vvNormClosed) && !vvSetup && !loading && (
              <div className="mb-3 max-w-[92%] rounded-2xl border border-line bg-bg-card p-3">
                <div className="text-sm text-ink leading-snug mb-2.5">
                  {vvChoice ? 'Что собрать во ВкусВилле?' : 'Собрать на целый день?'}
                </div>
                <div className="flex flex-col gap-1.5">
                  {VV_MODES.filter((m) => vvChoice || m.key !== 'rest').map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      onClick={() => openVvSetup(m.key)}
                      className="w-full px-3 py-2.5 rounded-xl bg-bg-elevated border border-line text-left active:scale-[0.98] transition-all flex items-baseline justify-between gap-3"
                    >
                      <span className="text-sm text-ink">{m.label}</span>
                      <span className="text-[11px] text-ink-dim shrink-0">{m.hint}</span>
                    </button>
                  ))}
                </div>
                <div className="text-[10px] text-ink-dim mt-2.5 leading-snug">{VV_DISCLAIMER}</div>
              </div>
            )}
            {askVkusvill && (
              <div className="mb-3 max-w-[92%] rounded-2xl border border-line bg-bg-card p-3">
                <div className="text-sm text-ink leading-snug mb-2.5">
                  Кстати, рядом с тобой есть ВкусВилл? Могу подбирать продукты прямо оттуда и собирать корзину.
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={async () => { await setVkusvill(true); setVvChoice(true); }}
                    className="px-4 py-2 rounded-xl bg-emerald-glow text-bg text-sm font-medium active:scale-95 transition-all"
                  >
                    Да, собери
                  </button>
                  <button
                    type="button"
                    onClick={() => setVkusvill(false)}
                    className="px-4 py-2 rounded-xl bg-bg-elevated border border-line text-ink-muted text-sm active:scale-95 transition-all"
                  >
                    Нет, не предлагай
                  </button>
                </div>
              </div>
            )}
            {awaitingFoodConfirm && (
              <div className="flex gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => sendMessage('Да, запиши')}
                  className="px-4 py-2 rounded-xl bg-emerald-glow text-bg text-sm font-medium active:scale-95 transition-all"
                >
                  Да, запиши
                </button>
                <button
                  type="button"
                  onClick={declineFood}
                  className="px-4 py-2 rounded-xl bg-bg-elevated border border-line text-ink-muted text-sm active:scale-95 transition-all"
                >
                  Нет, не надо
                </button>
              </div>
            )}
            {loading && (
              <div className="flex justify-start mb-3">
                <div className="bg-bg-card border border-line rounded-2xl px-4 py-3 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin text-emerald-glow" />
                  <span className="text-xs text-ink-muted">Дофи думает…</span>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="px-4 pb-4 pt-3 border-t border-line/50">
            {limitReached ? (
              <div className="text-center py-3">
                <div className="text-sm text-warn mb-2">
                  {blockedBy === 'month'
                    ? 'Сообщения на этот месяц закончились'
                    : `На сегодня хватит — ${limit} сообщений`}
                </div>
                <div className="text-xs text-ink-dim mb-2">
                  {blockedBy === 'month'
                    ? 'Обновятся первого числа. Всё остальное работает как обычно.'
                    : 'Завтра снова смогу отвечать. Еду записывать можно без ограничений.'}
                </div>
                {/* Третий выход, кроме «подожди» и «купи подписку»: докупить сообщения */}
                <div className="mt-3 text-left">
                  <div className="text-xs font-medium text-ink mb-2 text-center">Или докупи сообщения — продолжим сейчас</div>
                  <div className="grid grid-cols-2 gap-2">
                    {CHAT_PACKS.map((pack) => (
                      <button
                        key={pack.id}
                        onClick={() => buyPack(pack)}
                        disabled={!!buying}
                        className={`rounded-xl border p-3 text-center transition-transform active:scale-[0.98] disabled:opacity-60 ${
                          pack.best ? 'border-emerald-glow/50 bg-emerald-glow/10' : 'border-line bg-bg-card'
                        }`}
                      >
                        <div className="font-display font-bold text-base">{pack.messages} сообщений</div>
                        <div className="text-sm text-emerald-glow font-medium">{pack.price} ₽</div>
                        <div className="text-[10px] text-ink-dim mt-0.5">
                          {buying === pack.id ? 'Открываю оплату…' : pack.note}
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-ink-dim mt-2 text-center leading-snug">
                    Не сгорают и тратятся, только когда лимит исчерпан. Оплата разовая.
                    После оплаты вернись в чат — сообщения появятся сами.
                  </p>
                </div>
                {!isPaidPlan && (
                  <button
                    onClick={() => nav('/paywall')}
                    className="text-xs text-emerald-glow hover:underline mt-3"
                  >
                    С подпиской сообщений больше
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Обычный лимит упёрся, но есть докупленные — человек должен видеть,
                    что сейчас тратится запас и сколько его осталось */}
                {!unlimited && extra > 0 && limitHit && (
                  <div className="text-[11px] text-ink-dim text-center mb-2">
                    {limitHit === 'month' ? 'Лимит на месяц исчерпан' : 'Лимит на сегодня исчерпан'} — идут докупленные,
                    осталось <span className="text-emerald-glow font-medium">{extra}</span>
                  </div>
                )}
                {/* 🆕 Чипы-подсказки (видны если есть сообщения и не идёт запись) */}
                {messages.length > 0 && !isRecording && (
                  <div className="-mx-4 px-4 mb-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
                    <div className="flex gap-1.5" style={{ width: 'max-content' }}>
                      {suggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            if ('to' in suggestion && suggestion.to) { nav(suggestion.to); return; }
                        if ('action' in suggestion && suggestion.action === 'vv_choice') { setVvChoice(true); haptic('light'); return; }
                        if ('action' in suggestion && suggestion.action) { sendMessage(suggestion.text, String(suggestion.action)); return; }
                        setInput(suggestion.text);
                            haptic('light');
                          }}
                          disabled={loading}
                          className="shrink-0 px-3 py-1.5 rounded-full bg-bg-elevated border border-line text-[11px] text-ink-muted hover:border-emerald-glow/40 hover:text-emerald-glow active:scale-95 transition-all disabled:opacity-40 flex items-center gap-1.5"
                        >
                          <span className="text-xs">{suggestion.emoji}</span>
                          <span className="whitespace-nowrap">{suggestion.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  onClick={toggleListen}
                  aria-pressed={listenMode}
                  className={`mb-2 px-3 py-1.5 rounded-full border text-[11px] flex items-center gap-1.5 transition-colors ${
                    listenMode
                      ? 'border-emerald-glow/60 bg-emerald-glow/10 text-emerald-glow'
                      : 'border-line text-ink-muted'
                  }`}
                >
                  🤍 {listenMode ? 'Просто слушаю — нажми, чтобы выключить' : 'Просто выслушай, без советов'}
                </button>
                <div className="flex gap-2 items-end mb-2">
                  <input
  type="text"
  value={input}
  onChange={(e) => setInput(e.target.value)}
  onKeyDown={(e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (canSend) sendMessage();
    }
  }}
  onFocus={() => {
    const tg = (window as any).Telegram?.WebApp;
    if (tg) {
      tg.expand();
      setTimeout(() => tg.expand(), 300);
    }
  }}
  placeholder={isRecording ? 'Говори…' : 'Напиши Дофи…'}
  disabled={loading}
  className={`input flex-1 text-sm ${isRecording ? 'border-emerald-glow animate-pulse' : ''}`}
/>
                  {/* Кнопка «Записать по фото» убрана 07.10.2026 (Слава: «фото из чата
                      мы убирали — зачем кнопка»). Фото еды — с главной и из дневника.
                      Микрофон переехал сюда иконкой, отдельная строка под полем не нужна. */}
                  {voiceSupported && (
                    <button
                      type="button"
                      onClick={toggleRecording}
                      disabled={loading}
                      className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                        isRecording
                          ? 'bg-emerald-glow/20 text-emerald-glow animate-pulse'
                          : 'bg-bg-elevated text-ink-muted hover:text-emerald-glow'
                      }`}
                      aria-label={isRecording ? 'Остановить запись' : 'Сказать голосом'}
                    >
                      {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => sendMessage()}
                    disabled={!canSend}
                    className="w-11 h-11 rounded-xl bg-emerald-glow text-bg flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed shrink-0 active:scale-95 transition-all"
                    aria-label="Отправить"
                  >
                    <Send size={18} />
                  </button>
                </div>
                {!voiceSupported && (
                  <p className="text-[10px] text-ink-dim text-center mt-2">
                    💡 Голос недоступен. Открой в Chrome или Safari.
                  </p>
                )}
                {isRecording && (
                  <div className="flex items-center justify-center gap-2 mt-2 text-xs text-emerald-glow">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-glow animate-pulse" />
                    Говори в микрофон…
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
